import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Flex,
  Heading,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  Badge,
  useToast
} from '@chakra-ui/react';
import { AppContext } from '../AppContext';
import { callApi } from '../../callApi';

const RuleExecutionStats = () => {
  const [stats, setStats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [days, setDays] = useState(30);
  
  const { accessToken } = useContext(AppContext);
  const toast = useToast();

  useEffect(() => {
    const fetchStats = async () => {
      if (!accessToken) return;
      
      setIsLoading(true);
      try {
        const response = await callApi(`/api/v1/deterministic-rules/executions/summary?days=${days}`, accessToken);
        
        if (!response.ok) throw new Error('Failed to fetch execution stats');
        
        const data = await response.json();
        setStats(data);
      } catch (error) {
        toast({
          title: 'Error fetching stats',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [accessToken, days, toast]);

  return (
    <Box bg="white" borderRadius="md" boxShadow="sm" borderWidth="1px" p={4}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="md" color="gray.700">Rule Execution Statistics</Heading>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Monitor how often deterministic rules are firing and their impact on cases.
          </Text>
        </Box>
        <Flex align="center">
          <Text fontSize="sm" fontWeight="medium" mr={3} color="gray.600">Time Range:</Text>
          <Select 
            value={days} 
            onChange={(e) => setDays(Number(e.target.value))}
            width="150px"
            size="sm"
            bg="white"
          >
            <option value={1}>Last 24 Hours</option>
            <option value={3}>Last 3 Days</option>
            <option value={7}>Last 7 Days</option>
            <option value={30}>Last 30 Days</option>
          </Select>
        </Flex>
      </Flex>

      {isLoading ? (
        <Flex justify="center" align="center" p={10}>
          <Spinner size="xl" color="blue.500" />
        </Flex>
      ) : stats.length === 0 ? (
        <Text textAlign="center" color="gray.500" p={5}>
          No rule executions found for the selected time range.
        </Text>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Rule Type</Th>
                <Th>Action Taken</Th>
                <Th>Target Code</Th>
                <Th>Parameters / Conditions</Th>
                <Th isNumeric>Cases Applied To</Th>
              </Tr>
            </Thead>
            <Tbody>
              {stats.map((stat, index) => (
                <Tr key={index} _hover={{ bg: 'gray.50' }}>
                  <Td>
                    <Badge colorScheme={stat.rule_type === 'system' ? 'gray' : 'purple'} variant="solid">
                      {stat.rule_type}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme="blue" variant="subtle" textTransform="none">
                      {stat.action_taken}
                    </Badge>
                  </Td>
                  <Td>{stat.target_code || 'N/A'}</Td>
                  <Td>
                    <Box fontSize="xs" color="gray.600">
                      {/* Render Target Info for Payer/Carrier rules */}
                      {(stat.rule_type === 'payer' || stat.rule_type === 'carrier') && (
                        <Box mb={(stat.details && Object.keys(stat.details).length > 0) || stat.conditions ? 2 : 0}>
                          <Text>
                            <strong>{stat.rule_type === 'payer' ? 'Target Payer' : 'Target Carrier'}:</strong> {stat.rule_name}
                          </Text>
                        </Box>
                      )}

                      {/* Render Action Details */}
                      {stat.details && Object.keys(stat.details).length > 0 && (
                        <Box mb={stat.conditions ? 2 : 0}>
                          <Text fontWeight="bold" color="gray.700" mb={1}>Action Params:</Text>
                          {Object.entries(stat.details).map(([key, value]) => (
                            <Text key={key}>
                              <strong>{key}:</strong> {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </Text>
                          ))}
                        </Box>
                      )}
                      
                      {/* Render Dynamic Conditions */}
                      {stat.conditions && (
                        <Box>
                          <Text fontWeight="bold" color="gray.700" mb={1}>Conditions:</Text>
                          <Text as="pre" whiteSpace="pre-wrap" fontFamily="monospace" fontSize="2xs" bg="gray.50" p={2} borderRadius="sm" borderWidth="1px" borderColor="gray.200">
                            {(() => {
                              // Simple recursive function to format JSON as YAML-like text
                              const formatYaml = (obj, indent = 0) => {
                                if (obj === null || obj === undefined) return 'null';
                                if (typeof obj !== 'object') return String(obj);
                                
                                const spaces = ' '.repeat(indent);
                                let result = '';
                                
                                if (Array.isArray(obj)) {
                                  obj.forEach(item => {
                                    result += `\n${spaces}- ${formatYaml(item, indent + 2).trim()}`;
                                  });
                                } else {
                                  Object.entries(obj).forEach(([key, value]) => {
                                    if (typeof value === 'object' && value !== null) {
                                      result += `\n${spaces}${key}:${formatYaml(value, indent + 2)}`;
                                    } else {
                                      result += `\n${spaces}${key}: ${value}`;
                                    }
                                  });
                                }
                                return result;
                              };
                              return formatYaml(stat.conditions).trim();
                            })()}
                          </Text>
                        </Box>
                      )}

                      {/* Render None if all are empty */}
                      {(!stat.details || Object.keys(stat.details).length === 0) && !stat.conditions && stat.rule_type !== 'payer' && stat.rule_type !== 'carrier' && (
                        <Text color="gray.400">None</Text>
                      )}
                    </Box>
                  </Td>
                  <Td isNumeric fontWeight="bold" color="blue.600">
                    {stat.cases_applied_to}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default RuleExecutionStats;